import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Insight } from '../../types/insight';
import { RootState } from '../index';
import { fetchInsights as fetchInsightsApi, toggleLikeInsight, togglePinInsight } from '../../services/insightApi';

interface InsightState {
  insights: Insight[];
  loading: boolean;
  error: string | null;
}

const initialState: InsightState = {
  insights: [],
  loading: false,
  error: null
};

// Async thunks
export const fetchInsights = createAsyncThunk(
  'insights/fetchInsights',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchInsightsApi();
    } catch (error) {
      return rejectWithValue('Failed to fetch insights');
    }
  }
);

export const likeInsight = createAsyncThunk(
  'insights/likeInsight',
  async (insightId: string, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const insightState = state.insights as InsightState;
      const insight = insightState.insights.find((i: Insight) => i.id === insightId);
      
      if (!insight) {
        throw new Error('Insight not found');
      }
      
      // Call API to toggle like status
      const result = await toggleLikeInsight(insightId, insight.isLiked);
      
      if (result.success) {
        return {
          insightId,
          isLiked: !insight.isLiked
        };
      } else {
        throw new Error('Failed to update like status');
      }
    } catch (error) {
      return rejectWithValue('Failed to like insight');
    }
  }
);

export const pinInsight = createAsyncThunk(
  'insights/pinInsight',
  async (insightId: string, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const insightState = state.insights as InsightState;
      const insight = insightState.insights.find((i: Insight) => i.id === insightId);
      
      if (!insight) {
        throw new Error('Insight not found');
      }
      
      // Call API to toggle pin status
      const result = await togglePinInsight(insightId, insight.isPinned);
      
      if (result.success) {
        return {
          insightId,
          isPinned: !insight.isPinned
        };
      } else {
        throw new Error('Failed to update pin status');
      }
    } catch (error) {
      return rejectWithValue('Failed to pin insight');
    }
  }
);

const insightSlice = createSlice({
  name: 'insights',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch insights
      .addCase(fetchInsights.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInsights.fulfilled, (state, action: PayloadAction<Insight[]>) => {
        state.insights = action.payload;
        state.loading = false;
      })
      .addCase(fetchInsights.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Like insight
      .addCase(likeInsight.fulfilled, (state, action) => {
        const { insightId, isLiked } = action.payload;
        const insight = state.insights.find(i => i.id === insightId);
        
        if (insight) {
          insight.isLiked = isLiked;
          insight.likes += isLiked ? 1 : -1;
        }
      })
      
      // Pin insight
      .addCase(pinInsight.fulfilled, (state, action) => {
        const { insightId, isPinned } = action.payload;
        const insight = state.insights.find(i => i.id === insightId);
        
        if (insight) {
          insight.isPinned = isPinned;
        }
      });
  }
});

// Selectors
export const selectInsights = (state: RootState) => (state.insights as InsightState).insights;
export const selectInsightsLoading = (state: RootState) => (state.insights as InsightState).loading;
export const selectInsightsError = (state: RootState) => (state.insights as InsightState).error;

// Mock data - will be removed once API integration is complete
/* const mockInsights: Insight[] = [
  {
    id: '1',
    title: 'The Forgotten Coast',
    description: 'Lorem ipsum dolor sit amet consectetur adipiscing elit ut at massa mi. Aliquam in hendrerit urna. Pellentesque sit amet sapien. Lorem ipsum dolor sit amet consectetur adipiscing hendrerit.',
    imageUrl: '/images/insights/kayak.jpg',
    brand: {
      id: 'b1',
      name: 'Kayak',
      logo: '/images/brands/kayak.png'
    },
    likes: 124,
    comments: 32,
    isLiked: false,
    isPinned: false,
    createdAt: '2025-05-28T10:30:00Z'
  },
  {
    id: '2',
    title: 'Mountain Exploration',
    description: 'Discover the hidden gems of the mountain range with our expert guides. Experience breathtaking views and challenging trails designed for adventurers of all levels.',
    imageUrl: '/images/insights/mountain.jpg',
    brand: {
      id: 'b2',
      name: 'Trek Adventures',
      logo: '/images/brands/trek.png'
    },
    likes: 89,
    comments: 17,
    isLiked: true,
    isPinned: false,
    createdAt: '2025-05-27T14:20:00Z'
  },
  {
    id: '3',
    title: 'Urban Photography Guide',
    description: 'Master the art of urban photography with these essential tips and tricks. Learn how to capture the essence of city life through your lens.',
    imageUrl: '/images/insights/photography.jpg',
    brand: {
      id: 'b3',
      name: 'PixelPro',
      logo: '/images/brands/pixelpro.png'
    },
    likes: 215,
    comments: 42,
    isLiked: false,
    isPinned: true,
    createdAt: '2025-05-25T09:15:00Z'
  },
  {
    id: '4',
    title: 'Sustainable Fashion Trends',
    description: 'Explore the latest trends in sustainable fashion and how eco-friendly materials are revolutionizing the industry. Discover brands making a difference.',
    imageUrl: '/images/insights/fashion.jpg',
    brand: {
      id: 'b4',
      name: 'EcoStyle',
      logo: '/images/brands/ecostyle.png'
    },
    likes: 167,
    comments: 28,
    isLiked: false,
    isPinned: false,
    createdAt: '2025-05-23T16:45:00Z'
  },
  {
    id: '5',
    title: 'Culinary Adventures',
    description: 'Embark on a global culinary journey with these exotic recipes from around the world. Learn techniques from master chefs and impress your guests.',
    imageUrl: '/images/insights/culinary.jpg',
    brand: {
      id: 'b5',
      name: 'GourmetWorld',
      logo: '/images/brands/gourmet.png'
    },
    likes: 142,
    comments: 36,
    isLiked: true,
    isPinned: false,
    createdAt: '2025-05-21T11:30:00Z'
  },
  {
    id: '6',
    title: 'Digital Nomad Lifestyle',
    description: 'Learn how to balance work and travel as a digital nomad. Tips for productivity, finding accommodations, and building a sustainable remote career.',
    imageUrl: '/images/insights/nomad.jpg',
    brand: {
      id: 'b6',
      name: 'RemoteLife',
      logo: '/images/brands/remote.png'
    },
    likes: 198,
    comments: 45,
    isLiked: false,
    isPinned: true,
    createdAt: '2025-05-19T08:20:00Z'
  }
]; */

export default insightSlice.reducer;
